import { http } from "./http";

/**
 * Admin-only API client for the recognizer-management page. Mirrors
 * the API DTOs 1:1 — the SPA needs the customer/global scope marker
 * so it can render override badges and clone affordances.
 */

export interface RecognizerListItem {
  recognizerId: number;
  recognizerKey: string;
  displayLabel: string | null;
  customerId: number | null;
  isActive: boolean;
  sortOrder: number | null;
  intentCount: number;
}

export interface RecognizerIntentRow {
  recognizerIntentId: number | null;
  intentKey: string;
  displayLabel: string | null;
  description: string | null;
  sortOrder: number | null;
}

export interface RecognizerSlotRow {
  recognizerSlotId: number | null;
  slotKey: string;
  displayLabel: string | null;
  dataType: string | null;
  isRequired: boolean;
  sortOrder: number | null;
}

export interface RecognizerDetail {
  recognizerId: number;
  recognizerKey: string;
  displayLabel: string | null;
  description: string | null;
  customerId: number | null;
  llmModel: string | null;
  systemPrompt: string | null;
  outputSchema: string | null;
  isActive: boolean;
  sortOrder: number | null;
  intents: RecognizerIntentRow[];
  slots: RecognizerSlotRow[];
}

export interface RecognizerUpsertPayload {
  recognizerKey: string;
  displayLabel: string | null;
  description: string | null;
  customerId: number | null;
  llmModel: string | null;
  systemPrompt: string | null;
  outputSchema: string | null;
  isActive: boolean;
  sortOrder: number | null;
  intents: RecognizerIntentRow[];
  slots: RecognizerSlotRow[];
}

export function listRecognizers(customerId?: number | null): Promise<{ items: RecognizerListItem[] }> {
  const qs = customerId != null ? `?customerId=${encodeURIComponent(String(customerId))}` : "";
  return http<{ items: RecognizerListItem[] }>(`/api/admin/recognizers${qs}`);
}

export function getRecognizer(id: number): Promise<RecognizerDetail> {
  return http<RecognizerDetail>(`/api/admin/recognizers/${id}`);
}

export function createRecognizer(payload: RecognizerUpsertPayload): Promise<{ id: number }> {
  return http<{ id: number }>("/api/admin/recognizers", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateRecognizer(id: number, payload: RecognizerUpsertPayload): Promise<{ result: boolean }> {
  return http<{ result: boolean }>(`/api/admin/recognizers/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function deleteRecognizer(id: number): Promise<{ result: boolean }> {
  return http<{ result: boolean }>(`/api/admin/recognizers/${id}`, { method: "DELETE" });
}

export function cloneRecognizerForCustomer(
  sourceId: number,
  targetCustomerId: number,
): Promise<{ id: number }> {
  return http<{ id: number }>(`/api/admin/recognizers/${sourceId}/clone`, {
    method: "POST",
    body: JSON.stringify({ targetCustomerId }),
  });
}

export interface RecognizerTestPromptMessage {
  role: string;
  content: string;
}

export interface RecognizerTestUsage {
  promptTokens?: number;
  completionTokens?: number;
  totalTokens?: number;
}

export interface RecognizerTestResult {
  intent: string | null;
  intentMatched: boolean;
  confidence: number | null;
  slots: Record<string, unknown>;
  modelUsed: string;
  latencyMs: number;
  rawJson: string;
  usage?: RecognizerTestUsage | null;
  promptMessages: RecognizerTestPromptMessage[];
}

export interface RecognizerTestRequest {
  recognizerKey: string;
  displayLabel: string | null;
  description: string | null;
  llmModel: string | null;
  systemPrompt: string | null;
  outputSchema: string | null;
  intentKeys: string[];
  slots: Array<{ slotKey: string; dataType: string | null; isRequired: boolean }>;
  inputText: string;
}

export function testRecognizer(payload: RecognizerTestRequest): Promise<RecognizerTestResult> {
  return http<RecognizerTestResult>(`/api/admin/recognizers/test`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
